import type { Config, SheetConfig, SheetMapping, SegmentationOptions, GlobalOptions } from '../types';

export function createSheetConfig(mapping: SheetMapping): SheetConfig {
  const targetColumns: Record<string, string> = {};
  for (const entry of mapping.targetColumns) {
    if (!entry.locale.trim()) continue;
    targetColumns[entry.locale.trim()] = (entry.column || '').trim().toUpperCase();
  }

  return {
    namePattern: mapping.sheetName,
    sourceColumns: mapping.sourceColumn ? [mapping.sourceColumn.toUpperCase()] : [],
    targetColumns: Object.keys(targetColumns).length ? targetColumns : undefined,
    createTargetIfMissing: mapping.targetColumns.some((entry) => !entry.column.trim()),
    headerRow: mapping.headerRow,
    valuesStartRow: mapping.valuesStartRow,
    metadataRows: mapping.metadataRows,
    skipHiddenRows: mapping.skipHiddenRows,
    skipHiddenColumns: mapping.skipHiddenColumns,
    translateComments: mapping.translateComments,
    treatMergedRegions: mapping.treatMerged,
    preserveStyles: mapping.preserveStyles,
    excludeColors: mapping.excludeColors,
    inlineCodeRegexes: mapping.inlineCodeRegexes,
    sourceLocale: mapping.sourceLocale,
    html: {
      enabled: mapping.htmlEnabled,
      translatableTags: mapping.htmlTranslatableTags
        ? mapping.htmlTranslatableTags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : []
    }
  };
}

export function buildConfig(
  sheetMappings: SheetMapping[],
  globalOptions: GlobalOptions,
  segmentation: SegmentationOptions
): { config: Config; serialized: string } {
  const enabledSheets = sheetMappings.filter((sheet) => sheet.enabled && sheet.sourceColumn);
  const sheetConfigs = enabledSheets.map(createSheetConfig);

  const config: Config = {
    workbook: { sheets: sheetConfigs },
    global: {
      srcLang: globalOptions.srcLang,
      exportComments: globalOptions.exportComments,
      mergeFallback: globalOptions.mergeFallback,
      insertTargetPlacement: globalOptions.insertTargetPlacement,
      overwrite: globalOptions.overwrite,
      targetLocale: globalOptions.targetLocale,
      xliffVersion: globalOptions.xliffVersion,
      mtProvider: globalOptions.mtProvider
    },
    segmentation: {
      enabled: segmentation.enabled,
      rules: segmentation.mode === 'srx' ? { srxPath: 'uploaded.srx' } : 'builtin'
    }
  };

  const serialized = JSON.stringify(config, null, 2);
  return { config, serialized };
}

export function parseConfig(json: string): Config {
  return JSON.parse(json);
}

export function deriveSheetMapping(sheetCfg: SheetConfig): SheetMapping {
  const targets: Array<{ locale: string; column: string }> = [];
  if (sheetCfg.targetColumns) {
    for (const [locale, col] of Object.entries(sheetCfg.targetColumns)) {
      targets.push({ locale, column: col || '' });
    }
  }

  return {
    sheetName: sheetCfg.namePattern,
    enabled: true,
    sourceColumn: (sheetCfg.sourceColumns?.[0] ?? '').toUpperCase(),
    sourceLocale: sheetCfg.sourceLocale || 'en',
    targetColumns: targets,
    inlineCodeRegexes: sheetCfg.inlineCodeRegexes || [],
    headerRow: sheetCfg.headerRow ?? 1,
    valuesStartRow: sheetCfg.valuesStartRow ?? 2,
    metadataRows: sheetCfg.metadataRows || [],
    skipHiddenRows: sheetCfg.skipHiddenRows ?? false,
    skipHiddenColumns: sheetCfg.skipHiddenColumns ?? false,
    translateComments: sheetCfg.translateComments ?? false,
    treatMerged: sheetCfg.treatMergedRegions,
    preserveStyles: sheetCfg.preserveStyles ?? false,
    excludeColors: sheetCfg.excludeColors || [],
    htmlEnabled: sheetCfg.html?.enabled ?? true,
    htmlTranslatableTags: (sheetCfg.html?.translatableTags || []).join(', ')
  };
}

export function deriveGlobalOptions(config: Config): GlobalOptions {
  return {
    srcLang: config.global?.srcLang || 'en',
    exportComments: config.global?.exportComments ?? false,
    mergeFallback: config.global?.mergeFallback ?? 'source',
    insertTargetPlacement: config.global?.insertTargetPlacement ?? 'insertAfterSource',
    overwrite: config.global?.overwrite ?? true,
    targetLocale: config.global?.targetLocale,
    xliffVersion: config.global?.xliffVersion ?? '2.1',
    mtProvider: config.global?.mtProvider ?? 'none'
  };
}

export function deriveSegmentationOptions(config: Config, current: SegmentationOptions): SegmentationOptions {
  const rules = config.segmentation?.rules;
  const mode = rules && typeof rules === 'object' ? 'srx' : 'builtin';
  return {
    enabled: config.segmentation?.enabled ?? true,
    mode,
    srxText: mode === 'srx' ? current.srxText : undefined
  };
}
